import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../AppIcon';
import Button from '../../../ui/Button';
import Input from '../../../ui/Input';
import Select from '../../../ui/Select';

const CTASection = () => {
  const [selectedRole, setSelectedRole] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const roleOptions = [
    { value: 'corporate', label: 'Corporate Innovation Team' },
    { value: 'startup', label: 'Startup Founder/Team' },
    { value: 'intermediary', label: 'Professional Intermediary' },
    { value: 'field', label: 'Field Operations Staff' }
  ];

  const quickStartPaths = [
    {
      role: 'corporate',
      title: 'Corporate Innovation Teams',
      description: 'Launch your first scouting program and discover verified startups',
      icon: 'Building2',
      color: 'from-blue-500 to-blue-600',
      route: '/multi-role-dashboard',
      features: ['AI-powered matching', 'Verified startup profiles', 'ROI tracking', 'Risk assessment']
    },
    {
      role: 'startup',
      title: 'Growth-Stage Startups',
      description: 'Get discovered by corporations and access partnership opportunities',
      icon: 'Rocket',
      color: 'from-emerald-500 to-emerald-600',
      route: '/program-discovery-engine',
      features: ['Corporate program access', 'Profile verification', 'Partnership tracking', 'Growth resources']
    },
    {
      role: 'intermediary',
      title: 'Professional Intermediaries',
      description: 'Start earning referral commissions through verified matchmaking',
      icon: 'Users',
      color: 'from-purple-500 to-purple-600',
      route: '/referral-performance-center',
      features: ['Performance analytics', 'Commission tracking', 'Network management', 'Success optimization']
    },
    {
      role: 'field',
      title: 'Field Operations Staff',
      description: 'Begin collecting and verifying startup data in your region',
      icon: 'MapPin',
      color: 'from-orange-500 to-orange-600',
      route: '/real-time-scouting-interface',
      features: ['Mobile verification', 'Offline capabilities', 'Quality scoring', 'Real-time updates']
    }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    // Reset form or show success message
    alert('Thank you! We\'ll be in touch within 24 hours to get you started.');
    setEmail('');
    setCompany('');
    setSelectedRole('');
  };

  const selectedPath = quickStartPaths?.find(path => path?.role === selectedRole);

  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-64 h-64 bg-blue-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main CTA */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-white/10 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Sparkles" size={16} />
            <span>Ready to Transform Innovation?</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Start Your Innovation Journey Today
          </h2>
          
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
            Join 29,200+ organizations already using ScoutBridge to discover breakthrough partnerships, 
            generate revenue, and accelerate innovation through intelligent matching.
          </p>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            {[
              { value: '< 5 min', label: 'Setup Time' },
              { value: '48 hrs', label: 'First Match' },
              { value: '94.2%', label: 'Success Rate' },
              { value: '$0', label: 'Setup Cost' }
            ]?.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl lg:text-3xl font-bold text-white">{stat?.value}</div>
                <div className="text-white/70 text-sm">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Registration Form */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 lg:p-12 border border-white/20">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Form Section */}
              <div>
                <h3 className="text-2xl font-bold text-white mb-6">
                  Get Started in Minutes
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <Select
                    label="I am a..."
                    options={roleOptions}
                    value={selectedRole}
                    onChange={setSelectedRole}
                    placeholder="Select your role"
                    required
                    className="text-white"
                  />

                  <Input
                    label="Work Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    placeholder="your.email@company.com"
                    required
                    className="text-white"
                  />

                  <Input
                    label="Company/Organization"
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e?.target?.value)}
                    placeholder="Your organization name"
                    required
                    className="text-white"
                  />

                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    fullWidth
                    loading={isSubmitting}
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="bg-gradient-to-r from-primary to-accent hover:shadow-lg"
                  >
                    {isSubmitting ? 'Setting Up Your Account...' : 'Start Free Trial'}
                  </Button>

                  <p className="text-xs text-white/70 text-center">
                    No credit card required • 14-day free trial • Cancel anytime
                  </p>
                </form>
              </div>

              {/* Preview Section */}
              <div>
                {selectedPath ? (
                  <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${selectedPath?.color} flex items-center justify-center`}>
                        <Icon name={selectedPath?.icon} size={24} color="white" />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-white">{selectedPath?.title}</h4>
                        <p className="text-white/70 text-sm">Your personalized dashboard</p>
                      </div>
                    </div>
                    
                    <p className="text-white/90 mb-4">{selectedPath?.description}</p>
                    
                    <div className="space-y-2 mb-6">
                      <h5 className="text-sm font-medium text-white">What you'll get:</h5>
                      {selectedPath?.features?.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <Icon name="Check" size={14} className="text-emerald-400" />
                          <span className="text-sm text-white/80">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    <Link
                      to={selectedPath?.route}
                      className="inline-flex items-center text-white hover:text-white/80 transition-colors duration-200"
                    >
                      <span className="text-sm font-medium">Preview Dashboard</span>
                      <Icon name="ExternalLink" size={14} className="ml-1" />
                    </Link>
                  </div>
                ) : (
                  <div className="bg-white/5 rounded-xl p-6 border border-white/10 text-center">
                    <Icon name="Users" size={48} className="text-white/50 mx-auto mb-4" />
                    <h4 className="text-lg font-semibold text-white mb-2">
                      Choose Your Role
                    </h4>
                    <p className="text-white/70 text-sm">
                      Select your role above to see a personalized preview of your ScoutBridge experience.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Alternative Actions */}
        <div className="mt-16 text-center">
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <Icon name="Play" size={32} className="text-white mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-white mb-2">Watch Demo</h4>
              <p className="text-white/70 text-sm mb-4">
                See ScoutBridge in action with a 5-minute product walkthrough
              </p>
              <Button variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10">
                Play Video
              </Button>
            </div>

            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <Icon name="Calendar" size={32} className="text-white mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-white mb-2">Book Consultation</h4>
              <p className="text-white/70 text-sm mb-4">
                Schedule a personalized demo with our innovation experts
              </p>
              <Button variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10">
                Schedule Call
              </Button>
            </div>

            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <Icon name="FileText" size={32} className="text-white mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-white mb-2">Download Guide</h4>
              <p className="text-white/70 text-sm mb-4">
                Get our comprehensive innovation matching strategy guide
              </p>
              <Button variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10">
                Get Guide
              </Button>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-16 pt-8 border-t border-white/20">
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-70">
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={20} className="text-white" />
              <span className="text-white text-sm">SOC 2 Certified</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Award" size={20} className="text-white" />
              <span className="text-white text-sm">ISO 27001</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Lock" size={20} className="text-white" />
              <span className="text-white text-sm">Enterprise Security</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Globe" size={20} className="text-white" />
              <span className="text-white text-sm">Global Coverage</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;